import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@dorixona/ui'
import { formatPhone, nowMs } from '@dorixona/utils'
import { Sheet } from '../../ui/Sheet'
import { LoyaltyQr } from '../../ui/LoyaltyQr'
import { useScanCode, type ScanCode, type ScanMode } from './api'

export interface QrSheetProps {
  opened: boolean
  /** Строка для кассы из `GET /me/loyalty`; пустая, когда номер на кассе не принимают. */
  code: string
  phone: string
  /** Режим кассы из `GET /me/loyalty` (R-58). */
  scanMode: ScanMode
  onClose: () => void
  /** Гость: карты ещё нет — вместо кода просим войти. */
  guest?: boolean
  onSignIn?: () => void
}

/**
 * Карта лояльности на кассе — один код и для кэшбэка, и для оплаты баллами.
 *
 * В режиме `phone` касса узнаёт покупателя по номеру, и шторка показывает QR с номером. В остальных
 * режимах номер на кассе не принимается: шторка сразу берёт одноразовый код с разрешением списать
 * баллы. У кода минута на первое сканирование; дальше та же аптека может сканировать его снова, пока
 * не пробит чек (касса спрашивает баланс дважды за продажу). Вышла минута — «Получить новый код».
 * Гостю карты нет: шторка объясняет, что кэшбэк копится после входа, и кода не просит.
 */
export function QrSheet({
  opened,
  code,
  phone,
  scanMode,
  onClose,
  guest = false,
  onSignIn,
}: QrSheetProps) {
  const { t } = useTranslation(['account', 'common'])
  const scan = useScanCode()
  const [oneTime, setOneTime] = useState<ScanCode | null>(null)
  const [now, setNow] = useState(() => nowMs())
  const byPhone = scanMode === 'phone'

  const request = () => {
    scan.mutate(true, {
      onSuccess: (got) => {
        setNow(nowMs())
        setOneTime(got)
      },
    })
  }

  // Номер на кассе не принимают — код нужен сразу, как открыли шторку.
  // Гостю код не нужен: без входа сервер его не выдаст.
  const needsCode = opened && !guest && !byPhone && oneTime === null && scan.isIdle
  useEffect(() => {
    if (needsCode) request()
    // request меняется каждый рендер, а запросить нужно ровно по needsCode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsCode])

  // Обратный отсчёт: раз в секунду, пока на экране одноразовый код.
  useEffect(() => {
    if (oneTime === null) return undefined
    const timer = window.setInterval(() => {
      setNow(nowMs())
    }, 1000)
    return () => {
      window.clearInterval(timer)
    }
  }, [oneTime])

  const close = () => {
    setOneTime(null)
    scan.reset()
    onClose()
  }

  const left = oneTime ? Math.max(0, Math.ceil((oneTime.expiresAt - now) / 1000)) : 0

  if (guest) {
    return (
      <Sheet opened={opened} label={t('qr.sheet')} onClose={onClose}>
        <div className="qr-sheet">
          <h2 className="qr-sheet__title">{t('qr.guestTitle')}</h2>
          <p className="qr-sheet__hint">{t('qr.guestBody')}</p>
          <div className="qr-sheet__action">
            <Button onPress={onSignIn}>{t('common:actions.signIn')}</Button>
          </div>
        </div>
      </Sheet>
    )
  }

  return (
    <Sheet opened={opened} label={t('qr.sheet')} onClose={close}>
      <div className="qr-sheet">
        <h2 className="qr-sheet__title">{t('qr.title')}</h2>
        {byPhone ? (
          <>
            <p className="qr-sheet__hint">{t('qr.hint')}</p>
            <div className="qr-sheet__code">
              {code ? (
                <LoyaltyQr code={code} label={t('common:loyaltyQr')} />
              ) : (
                <p className="qr-sheet__hint">{t('qr.loading')}</p>
              )}
            </div>
            <p className="qr-sheet__phone num" data-selectable>
              {formatPhone(phone)}
            </p>
            <p className="qr-sheet__hint">{t('qr.fallback')}</p>
          </>
        ) : (
          <>
            <p className="qr-sheet__hint">{t('qr.codeHint')}</p>
            {oneTime && left > 0 ? (
              <>
                <div className="qr-sheet__code">
                  <LoyaltyQr code={oneTime.code} label={t('common:loyaltyQr')} />
                </div>
                <p className="qr-sheet__phone num" data-selectable>
                  {oneTime.code}
                </p>
                <p className="qr-sheet__hint" aria-live="polite">
                  {t('qr.expiresIn', { time: clock(left) })}
                </p>
              </>
            ) : null}
            {oneTime && left === 0 ? (
              <p className="qr-sheet__expired" role="status">
                {t('qr.expired')}
              </p>
            ) : null}
            {oneTime === null && !scan.isError ? (
              <p className="qr-sheet__hint qr-sheet__wait">{t('qr.codeLoading')}</p>
            ) : null}
            {scan.isError ? (
              <p className="qr-sheet__expired" role="alert">
                {t('qr.codeError')}
              </p>
            ) : null}
            {(oneTime && left === 0) || scan.isError ? (
              <div className="qr-sheet__actions">
                <Button disabled={scan.isPending} onPress={request}>
                  {t('qr.again')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Sheet>
  )
}

/** Сколько осталось, как на часах: «0:59», «0:07». */
function clock(seconds: number): string {
  return `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, '0')}`
}
